import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../packages/api-client';
import { ChartColumn, Loader, Sparkles, Check, ClipboardList, Download, Printer, TrendingUp, FileText, TriangleAlert, Target } from 'lucide-react';
import { StateView } from '../../packages/ui';
import '../../components/ChartFigure.css';

interface StatusReport {
  project_name: string;
  period: string;
  progress_percent: number;
  completed_count: number;
  open_count: number;
  overdue_count: number;
  risks: string[];
  next_week: string[];
  narrative: string;
  markdown?: string;
}

/** Server messages for the AI gate (backend internal/ai/gate.go). */
const AI_UNAVAILABLE = /AI is temporarily unavailable|monthly AI limit reached/i;

/**
 * StatusReportView — Reports › Status report. The progress figures are
 * measured by the server from the tasks the caller can see; the summary, risks
 * and next steps are AI-generated and labelled so. When the AI is off or out of
 * quota the server says so (shown verbatim); when the model call fails the
 * server still returns the measured figures with no narrative, and this view
 * says the narrative is missing instead of hiding it.
 */
export function StatusReportView({ projectId }: { projectId: string }) {
  const [period, setPeriod] = useState(7);
  const [report, setReport] = useState<StatusReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState<'' | 'copied' | 'failed'>('');

  const generate = async () => {
    setLoading(true); setError(''); setReport(null); setCopyState('');
    try {
      const { data } = await apiClient.post<StatusReport>(
        `/projects/${projectId}/reports/generate`,
        { period_days: period, format: 'markdown' }
      );
      setReport(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'The report request failed.');
    } finally { setLoading(false); }
  };

  const copyMarkdown = () => {
    if (!report?.markdown) return;
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(report.markdown!))
      .then(() => { setCopyState('copied'); setTimeout(() => setCopyState(''), 2000); })
      .catch(() => setCopyState('failed'));
  };

  const downloadMarkdown = () => {
    if (!report?.markdown) return;
    const blob = new Blob([report.markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.project_name.replace(/\s+/g, '-')}-status-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasNarrative = !!report && (!!report.narrative || (report.risks?.length ?? 0) > 0 || (report.next_week?.length ?? 0) > 0);
  const aiDown = AI_UNAVAILABLE.test(error);

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h2 className="report-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}><ChartColumn size={20} aria-hidden="true" /> Status report</h2>
        <p className="page-subtitle" style={{ margin: 0 }}>Progress figures are measured from your tasks; the summary, risks and next steps are written by AI.</p>
      </div>

      <div className="card report-controls no-print">
        <div className="report-period-row">
          <span className="form-label" id={`rp-${projectId}`}>Report period</span>
          <div className="report-period-chips" role="group" aria-labelledby={`rp-${projectId}`}>
            {[7, 14, 30].map(d => (
              <button key={d} type="button" className={`period-chip${period === d ? ' active' : ''}`}
                aria-pressed={period === d} onClick={() => setPeriod(d)}>
                Last {d} days
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn-primary btn-md" onClick={generate} disabled={loading} aria-busy={loading || undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          {loading
            ? <><Loader size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} /> Generating report…</>
            : <><Sparkles size={16} aria-hidden="true" /> Generate Report</>}
        </button>
      </div>

      {error && (aiDown
        ? <StateView state="error" title="AI isn't available for this workspace" message={<>{error}. No report was generated. Measured progress is on the Analytics view.</>} />
        : <StateView state="error" title="The report couldn't be generated" message={error} onRetry={generate} retrying={loading} />)}

      {!report && !error && !loading && (
        <StateView state="empty" title="No report generated yet" message="Pick a period and generate a report. Nothing is saved until you copy or download it." />
      )}

      {report && (
        <div className="report-result">
          <div className="report-export-bar no-print">
            <span className="report-period-label">{report.period}</span>
            <div className="report-export-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={copyMarkdown} disabled={!report.markdown} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                {copyState === 'copied'
                  ? <><Check size={14} aria-hidden="true" /> Copied</>
                  : <><ClipboardList size={14} aria-hidden="true" /> Copy Markdown</>}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={downloadMarkdown} disabled={!report.markdown} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Download size={14} aria-hidden="true" /> Download .md
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Printer size={14} aria-hidden="true" /> Print
              </button>
            </div>
          </div>
          {copyState === 'failed' && <p role="alert" className="rpt-muted">Couldn&apos;t copy to the clipboard. Use Download .md instead.</p>}

          <div className="report-card">
            <h2 className="report-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><TrendingUp size={18} aria-hidden="true" /> Progress <span className="rpt-tag">Measured</span></h2>
            <div className="report-stats-row">
              <div className="report-stat">
                <div className="report-stat-value">{report.progress_percent}%</div>
                <div className="report-stat-label">Complete</div>
              </div>
              <div className="report-stat">
                <div className="report-stat-value">{report.completed_count}</div>
                <div className="report-stat-label">Done this period</div>
              </div>
              <div className="report-stat">
                <div className="report-stat-value">{report.open_count}</div>
                <div className="report-stat-label">Open tasks</div>
              </div>
              <div className={`report-stat${report.overdue_count > 0 ? ' danger' : ''}`}>
                <div className="report-stat-value">{report.overdue_count}</div>
                <div className="report-stat-label">Overdue</div>
              </div>
            </div>
            <div className="report-progress-bar" aria-hidden="true">
              <div className="report-progress-fill" style={{ width: `${report.progress_percent}%` }} />
            </div>
          </div>

          {!hasNarrative && (
            <div className="rpt-notice" role="status">
              <strong>No AI narrative for this report.</strong> The AI summary, risks and next steps couldn&apos;t be generated
              (AI may not be configured for this workspace, or the model call failed). The progress figures above are measured and complete.
            </div>
          )}

          {report.narrative && (
            <div className="report-card">
              <h2 className="report-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FileText size={18} aria-hidden="true" /> Summary <span className="rpt-tag rpt-tag--ai">AI-generated</span></h2>
              <p className="report-narrative">{report.narrative}</p>
            </div>
          )}

          {report.risks?.length > 0 && (
            <div className="report-card report-card-danger">
              <h2 className="report-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><TriangleAlert size={18} aria-hidden="true" /> Risks <span className="rpt-tag rpt-tag--ai">AI-generated</span></h2>
              <ul className="report-list">
                {report.risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {report.next_week?.length > 0 && (
            <div className="report-card">
              <h2 className="report-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Target size={18} aria-hidden="true" /> Next steps <span className="rpt-tag rpt-tag--ai">AI-generated</span></h2>
              <ul className="report-list">
                {report.next_week.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * StatusReportPage — the old standalone /projects/:id/report route. It used to
 * render its own breadcrumb + page header, which dropped the full project-detail
 * chrome (Overview/Tasks/Board/…) and forced a Back click to return. The report
 * now lives as an in-page tab inside ProjectDetailPage, so this route just
 * redirects there, forwarding any existing query params.
 */
export function StatusReportPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!projectId) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'Report');
    navigate(`/projects/${projectId}?${next.toString()}`, { replace: true });
  }, [projectId, searchParams, navigate]);

  return null;
}
